import "@/App.css";
import {useEffect} from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

import Lightspeed from "@/pages/Lightspeed";
import Console from "@/pages/Console";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Sites from "@/pages/Sites";
import Apps from "@/pages/Apps";
import Files from "@/pages/Files";
import Links from "@/pages/Links";
import Routing from "@/pages/Routing";
import Backups from "@/pages/Backups";
import Monitoring from "@/pages/Monitoring";
import Logs from "@/pages/Logs";
import Users from "@/pages/Users";
import ServerControl from "@/pages/ServerControl";
import Settings from "@/pages/Settings";
import CloudRun from "@/pages/CloudRun";
import FreeDNS from "@/pages/FreeDNS";
import AWSHosting from "@/pages/AWSHosting";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center text-zinc-500 gap-2 font-mono text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Cannon…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/sites" element={<Sites />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/files" element={<Files />} />
        <Route path="/links" element={<Links />} />
        <Route path="/cloudrun" element={<CloudRun />} />
        <Route path="/aws" element={<AWSHosting />} />
        <Route path="/freedns" element={<FreeDNS />} />
        <Route path="/lightspeed" element={<Lightspeed />} />
        <Route path="/routing" element={<Routing />} />
        <Route path="/backups" element={<Backups />} />
        <Route path="/monitoring" element={<Monitoring />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/users" element={<Users />} />
        <Route path="/server" element={<ServerControl />} />
        <Route path="/console" element={<Console />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(()=>{window.dispatchEvent(new Event("cannon-ready"));},[]);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
